import {
    ArrowDownTrayIcon,
    DocumentIcon,
    PencilSquareIcon,
    SparklesIcon,
    TrashIcon
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import type { ChatMessage } from '../types/chat';
import type { Match, PerMessageMatches } from '../types/search';
import { useChatConfig } from '../context/ChatConfigContext';
import { ImageLightbox } from './ImageLightbox';
import { getFileCategory, isImageFile, isPdfFile, isVideoFile } from '../utils/fileTypeUtils';
import { FilePreviewModal } from './FilePreviewModal';
import { HtmlContent } from './HtmlContent';
import { DocContent, isDocEmpty } from './DocContent';
import { SeenReceipt } from './SeenReceipt';
import {
    displayName,
    formatTimestamp,
    getFileIcon,
    isOrtho,
    truncateFileName
} from '../utils/chatUtils';
import { canManageMessage, isAiEmail } from '../utils/messageAuthor';

interface FileAttachmentProps {
    name: string;
    url: string;
}

const ImageAttachment: React.FC<FileAttachmentProps> = ({ name, url }) => {
    const [previewOpen, setPreviewOpen] = useState(false);
    return (
        <>
            <button
                type="button"
                onClick={() => setPreviewOpen(true)}
                className="block overflow-hidden rounded-lg hover:opacity-90 transition-opacity focus:outline-none"
            >
                <img
                    src={url}
                    alt={name}
                    loading="lazy"
                    className="max-w-[240px] max-h-[180px] object-cover rounded-lg"
                />
            </button>
            {previewOpen && (
                <FilePreviewModal
                    type="image"
                    url={url}
                    name={name}
                    onClose={() => setPreviewOpen(false)}
                />
            )}
        </>
    );
};

const PdfAttachment: React.FC<FileAttachmentProps> = ({ name, url }) => {
    const [previewOpen, setPreviewOpen] = useState(false);
    return (
        <>
            <button
                type="button"
                onClick={() => setPreviewOpen(true)}
                className="group flex items-center gap-2.5 bg-white border border-gray-200 rounded-lg px-3 py-2.5 hover:bg-gray-50 hover:border-gray-300 transition-colors cursor-pointer max-w-[280px]"
            >
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-red-50 rounded-lg">
                    <DocumentIcon className="w-4 h-4 text-red-500" />
                </div>
                <div className="flex-1 min-w-0 text-left">
                    <span className="text-xs font-medium text-gray-900 truncate block" title={name}>
                        {truncateFileName(name)}
                    </span>
                    <span className="text-[10px] text-gray-400 uppercase">PDF</span>
                </div>
                <ArrowDownTrayIcon className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600 flex-shrink-0" />
            </button>
            {previewOpen && (
                <FilePreviewModal
                    type="pdf"
                    url={url}
                    name={name}
                    onClose={() => setPreviewOpen(false)}
                />
            )}
        </>
    );
};

const VideoAttachment: React.FC<FileAttachmentProps> = ({ name, url }) => {
    const [previewOpen, setPreviewOpen] = useState(false);
    return (
        <>
            <button
                type="button"
                onClick={() => setPreviewOpen(true)}
                className="group flex items-center gap-2.5 bg-white border border-gray-200 rounded-lg px-3 py-2.5 hover:bg-gray-50 hover:border-gray-300 transition-colors cursor-pointer max-w-[280px]"
            >
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-purple-50 rounded-lg">
                    <svg
                        className="w-4 h-4 text-purple-500"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                    </svg>
                </div>
                <div className="flex-1 min-w-0 text-left">
                    <span className="text-xs font-medium text-gray-900 truncate block" title={name}>
                        {truncateFileName(name)}
                    </span>
                    <span className="text-[10px] text-gray-400 uppercase">Video</span>
                </div>
                <svg
                    className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600 flex-shrink-0"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                >
                    <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                </svg>
            </button>
            {previewOpen && (
                <FilePreviewModal
                    type="video"
                    url={url}
                    name={name}
                    onClose={() => setPreviewOpen(false)}
                />
            )}
        </>
    );
};

const SimpleFileAttachment: React.FC<FileAttachmentProps> = ({ name, url }) => (
    <a
        href={url}
        onClick={(e) => {
            e.preventDefault();
            window.open(url, '_blank', 'noopener,noreferrer');
        }}
        target="_blank"
        rel="noopener noreferrer"
        className="group flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-2.5 py-2 hover:bg-gray-50 hover:border-gray-300 transition-colors cursor-pointer w-full max-w-full"
    >
        <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center bg-gray-100 rounded group-hover:bg-gray-200 transition-colors">
            <img src={getFileIcon(name)} alt="" className="w-3.5 h-3.5 object-contain" />
        </div>
        <div className="flex-1 min-w-0 overflow-hidden">
            <span className="text-[11px] font-medium text-gray-900 truncate block" title={name}>
                {truncateFileName(name)}
            </span>
        </div>
        <div className="flex-shrink-0 w-3.5 h-3.5 flex items-center justify-center text-gray-400 group-hover:text-gray-600 transition-colors">
            <ArrowDownTrayIcon className="w-3 h-3" />
        </div>
    </a>
);

const AttachmentRenderer: React.FC<FileAttachmentProps & { previewMode: 'preview' | 'newTab' }> = ({
    name,
    url,
    previewMode
}) => {
    if (previewMode === 'newTab') return <SimpleFileAttachment name={name} url={url} />;
    if (isImageFile(name)) return <ImageAttachment name={name} url={url} />;
    if (isPdfFile(name)) return <PdfAttachment name={name} url={url} />;
    if (isVideoFile(name)) return <VideoAttachment name={name} url={url} />;
    return <SimpleFileAttachment name={name} url={url} />;
};

const renderHighlightedText = (
    text: string | undefined,
    matches: Match[],
    activeMatchIdx: number
): React.ReactNode => {
    const value = (text || '').toString();
    if (matches.length === 0) return value;
    const nodes: React.ReactNode[] = [];
    let last = 0;
    for (const m of matches) {
        if (m.start > last) nodes.push(value.slice(last, m.start));
        const isActive = m.globalIdx === activeMatchIdx;
        nodes.push(
            <span
                key={`hl-${m.globalIdx}`}
                data-match-idx={m.globalIdx}
                className={`${isActive ? 'bg-yellow-300 ring-1 ring-yellow-400' : 'bg-yellow-200'} rounded px-0.5`}
            >
                {value.slice(m.start, m.end)}
            </span>
        );
        last = m.end;
    }
    if (last < value.length) nodes.push(value.slice(last));
    return nodes;
};

interface MessageBubbleProps {
    message: ChatMessage;
    msgIndex: number;
    perMessageMatches: PerMessageMatches;
    activeMatchIdx: number;
    searchQuery?: string;
    currentUserEmail?: string;
    onStartEdit?: (message: ChatMessage) => void;
    onDeleteMessage?: (message: ChatMessage) => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
    message,
    msgIndex,
    perMessageMatches,
    activeMatchIdx,
    searchQuery,
    currentUserEmail,
    onStartEdit,
    onDeleteMessage
}) => {
    const config = useChatConfig();
    const isMe = config.isCurrentUser(message.author?.email);
    const isAi = isAiEmail(message.author?.email);
    const isOrthoMsg = isOrtho(message.author?.email);
    const matches = perMessageMatches.get(msgIndex) || [];
    // The document wins over HTML wherever both exist. v1 sends only htmlBody,
    // v2 sends only bodyDoc, so in practice each feed takes one branch — but
    // the precedence has to be explicit, because a message that has both must
    // not render Monday's stale markup over the canonical body.
    const hasDoc = !!message.bodyDoc && !isDocEmpty(message.bodyDoc);
    const isHtml =
        !hasDoc && config.htmlRenderingEnabled && message.format === 'html' && !!message.htmlBody;
    const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

    // Ortho visual accent: teal ring on the avatar + faint teal bubble tint,
    // using the #007E87 family that matches the "Start monitoring" action color
    // already in the design system. No border on ortho bubbles.
    const orthoAvatarClasses = isOrthoMsg ? 'rounded-full ring-2 ring-[#007E87]' : '';

    const bubbleBgClass = isOrthoMsg
        ? 'bg-[#E6F4F5]'
        : isAi
          ? 'bg-yellow-50'
          : isMe
            ? 'bg-gray-200'
            : 'bg-gray-50';
    const bubbleBorderClass = isOrthoMsg
        ? ''
        : isAi
          ? 'border border-yellow-100'
          : isMe
            ? ''
            : 'border border-gray-100';

    const canManage = canManageMessage(
        message.author?.email,
        currentUserEmail,
        !!config.manageOdoStaffMessages
    );
    const canEdit = config.editingEnabled && !!onStartEdit && canManage;
    const canDelete = config.deletingEnabled && !!onDeleteMessage && canManage;
    const isSeen =
        !!config.otherSideLastSeenAt &&
        new Date(message.createdAt).getTime() <= new Date(config.otherSideLastSeenAt).getTime();

    return (
        <div className={`flex items-end gap-3 ${isMe ? 'justify-end' : 'justify-start'}`}>
            {!isMe && (
                <div className={`w-8 h-8 shrink-0 ${orthoAvatarClasses}`}>
                    {config.renderAvatar({
                        user: {
                            name: message.author?.name || 'Patient',
                            email: message.author?.email || ''
                        },
                        imageURL: message.author?.profilePictureUrl
                    })}
                </div>
            )}
            <div className={`flex flex-col min-w-0 max-w-[75%] ${isMe ? 'items-end' : ''}`}>
                {/* AI badge */}
                {isAi && (
                    <div className="flex items-center gap-1 mb-1">
                        <SparklesIcon className="w-3 h-3 text-yellow-500" />
                        <span className="text-[10px] font-semibold text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-full px-1.5 py-0.5">
                            AI
                        </span>
                    </div>
                )}

                <div
                    className={`relative ${bubbleBgClass} ${bubbleBorderClass} rounded-xl px-4 py-3 break-words overflow-hidden text-sm text-gray-900 space-y-2 w-full min-w-0`}
                >
                    <div className="min-w-0 overflow-hidden">
                        {hasDoc ? (
                            <DocContent
                                doc={message.bodyDoc!}
                                resolveFileUrl={config.resolveFileUrl}
                                onImageClick={setLightboxSrc}
                            />
                        ) : isHtml ? (
                            <HtmlContent
                                html={message.htmlBody!}
                                onImageClick={setLightboxSrc}
                                highlightQuery={searchQuery}
                                highlightActiveIdx={activeMatchIdx}
                                highlightStartIdx={matches.length > 0 ? matches[0].globalIdx : 0}
                            />
                        ) : (
                            <div className="whitespace-pre-wrap">
                                {renderHighlightedText(message.text, matches, activeMatchIdx)}
                            </div>
                        )}
                    </div>

                    {message.assets && message.assets.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                            {[...message.assets]
                                .sort((a, b) => {
                                    const order = { image: 0, video: 1, pdf: 2, other: 3 };
                                    return (
                                        order[getFileCategory(a.name)] -
                                        order[getFileCategory(b.name)]
                                    );
                                })
                                .map((a) => (
                                    <AttachmentRenderer
                                        key={a.url || a.name}
                                        name={a.name}
                                        url={a.url}
                                        previewMode={config.filePreviewMode}
                                    />
                                ))}
                        </div>
                    )}
                </div>

                {/* Timestamp + edit */}
                <div
                    className={`group/meta flex items-center gap-1.5 text-[11px] text-gray-400 mt-1 ${isMe ? 'justify-end' : 'justify-start'}`}
                >
                    <span>
                        {displayName(message.author?.name, message.author?.email)} &bull;{' '}
                        {formatTimestamp(message.createdAt)}
                        {message.editedAt && (
                            <span className="italic text-gray-300 ml-1">edited</span>
                        )}
                    </span>
                    {config.receiptsEnabled && isMe && <SeenReceipt seen={isSeen} />}
                    {canEdit && (
                        <button
                            onClick={() => onStartEdit?.(message)}
                            className="opacity-40 hover:opacity-100 p-0.5 rounded hover:bg-gray-200 transition-opacity"
                            title="Edit message"
                        >
                            <PencilSquareIcon className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                        </button>
                    )}
                    {canDelete && (
                        <button
                            onClick={() => onDeleteMessage?.(message)}
                            className="opacity-40 hover:opacity-100 p-0.5 rounded hover:bg-gray-200 transition-opacity"
                            title="Delete message"
                            aria-label="Delete message"
                        >
                            <TrashIcon className="w-3 h-3 text-gray-400 hover:text-red-600" />
                        </button>
                    )}
                </div>
            </div>
            {isMe && (
                <div className={`w-8 h-8 shrink-0 ${orthoAvatarClasses}`}>
                    {config.renderAvatar({
                        user: {
                            name: message.author?.name || 'You',
                            email: message.author?.email || ''
                        },
                        imageURL: message.author?.profilePictureUrl
                    })}
                </div>
            )}
            {lightboxSrc && (
                <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
            )}
        </div>
    );
};

export { MessageBubble };
